/* eslint-disable max-lines-per-function */
import { ChangeEventHandler, DragEventHandler, useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-hot-toast';

import { UploadIcon, DeleteIcon } from '../../';
import { Button } from '../button/button';
import { Spinner } from '../spinner/spinner';
import { Toast } from '../toast/toast';

interface UploadCardProps {
  file: File;
  onDelete: () => void;
}

const UploadCard = (props: UploadCardProps) => {
  const { file, onDelete } = props;

  const [image, setImage] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const reader = new FileReader();
    reader.addEventListener('loadstart', () => {
      setIsLoading(true);
    });
    reader.onloadend = () => {
      setIsLoading(false);
      setImage(reader.result as string);
    };
    reader.readAsDataURL(file);
  }, [file]);

  return (
    <div className="grid grid-cols-[3.125rem_auto_3.125rem] items-center rounded-lg border border-gray-300 bg-white p-2">
      <div className="flex size-12 items-center justify-center">
        {isLoading ? <Spinner /> : <img className="max-h-full max-w-full" src={image} alt={file.name} />}
      </div>
      <span className="overflow-hidden text-ellipsis pl-2 text-primary">{file.name}</span>
      <div className="ml-auto flex items-center">
        <button aria-label={`delete ${file.name}`} onClick={onDelete}>
          <DeleteIcon className="size-4 fill-gray-700" />
        </button>
      </div>
    </div>
  );
};

const validateFileSize = (maxSize: number, file: File): boolean => {
  const fileSize = file.size;
  return fileSize <= maxSize * 1024 * 1024;
};

export type FileUploadEvent = (files: File[]) => void;

export interface FileUploadProps {
  label?: string;
  buttonLabel?: string;
  allowedFileTypes?: string[];
  // In MB
  maxSize?: number;
  required?: boolean;
  helperText?: string;
  error?: string;
  onChange?: FileUploadEvent;
  disabled?: boolean;
  multiple?: boolean;
  name: string;
  value?: File[];
}

const handleUploadError = (error: string) => {
  toast.custom((t) => <Toast context={t}>{error}</Toast>);
};

// Parse file uploads event from click and drop events
const uploadParser = (files: FileList, allowedFileTypes?: string[], maxSize?: number) => {
  let uploads: File[] = [];
  for (let i = 0; i < files.length; i++) {
    const file = files.item(i);
    let isValid = true;
    if (file) {
      if (maxSize) {
        isValid = validateFileSize(maxSize, file);
        if (!isValid) {
          handleUploadError(`File size too large. (max size: ${maxSize}MB)`);
          continue;
        }
      }
      uploads = isValid ? [...uploads, file] : [...uploads];
    }
  }
  return uploads;
};

export const FileUpload = ({ buttonLabel = 'Upload photo', ...props }: FileUploadProps) => {
  const { onChange, allowedFileTypes, maxSize, label, required, helperText, error, disabled, multiple, name, value } =
    props;

  // Local state
  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Refs
  const input = useRef<HTMLInputElement>(null);

  // Handlers
  const saveUploads = useCallback(
    (uploads: File[]) => {
      // Prevent file reselection
      const existingFileNames = new Set(files.map((file) => file.name));
      const newFiles = uploads.filter((newFile) => !existingFileNames.has(newFile.name));

      const update = [...files, ...newFiles];
      setFiles([...update]);
      if (onChange) {
        onChange([...update]);
      }
      setIsLoading(false);
    },
    [onChange, files]
  );

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      if (!event.target.files) return;
      setIsLoading(true);
      const uploads = uploadParser(event.target.files, allowedFileTypes, maxSize);
      saveUploads(uploads);
      // This allows us to select same file after deleting it once.
      //  We are handling duplicate file selection using file name check.
      event.target.value = '';
    },
    [saveUploads, allowedFileTypes, maxSize]
  );

  const handleDrop: DragEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      event.preventDefault();
      if (!event.dataTransfer.files) return;
      setIsLoading(true);
      const uploads = uploadParser(event.dataTransfer.files, allowedFileTypes, maxSize);
      saveUploads(uploads);
    },
    [saveUploads, allowedFileTypes, maxSize]
  );

  const handleDragOver: DragEventHandler<HTMLDivElement> = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleDeleteFile = useCallback(
    (file: File) => () => {
      const index = files.indexOf(file);
      const newFiles = [...files];
      newFiles.splice(index, 1);
      setFiles([...newFiles]);
      if (onChange) {
        onChange([...newFiles]);
      }
    },
    [files, onChange]
  );

  // Renderers
  const renderFiles = useMemo(() => {
    return files.map((file, index) => {
      return <UploadCard key={index} file={file} onDelete={handleDeleteFile(file)} />;
    });
  }, [files, handleDeleteFile]);

  useEffect(() => {
    setFiles(value || []);
  }, [value]);

  return (
    <div className="text-gray-900" onDrop={handleDrop} onDragOver={handleDragOver}>
      <div className="flex flex-col pb-2">
        <div>
          <span className="font-medium">{label}</span>
          {!required && <span className="text-gray-700"> (optional)</span>}
        </div>
        {helperText && <span>{helperText}</span>}
        {error && <span className="text-red-600">{error}</span>}
      </div>

      <label htmlFor="file-upload">
        <input
          type="file"
          className="hidden"
          id="file-upload"
          multiple={multiple}
          onChange={handleChange}
          ref={input}
          accept={allowedFileTypes ? allowedFileTypes.join(',') : undefined}
          name={name}
          role="form"
        />
        <Button
          icon={UploadIcon}
          loading={isLoading}
          type="default"
          onClick={(e) => {
            e.preventDefault();
            if (input.current) {
              input.current.click();
            }
          }}
          disabled={disabled || isLoading}
          className="!w-full items-center justify-center space-x-2"
        >
          <span className="font-normal	">{buttonLabel}</span>
        </Button>
      </label>
      <div className="mt-2 flex flex-col gap-2">{renderFiles}</div>
    </div>
  );
};

export default FileUpload;
